'use client';

import { useState, useCallback, useEffect, useRef } from 'react';
import { Check, ChevronDown, Terminal } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from '@/components/ui/dropdown-menu';
import { copyToClipboard } from '@/lib/clipboard';

/* ── Types ── */

export interface DeployConfig {
  /** GitHub org or user that owns the template repo */
  templateOrg?: string;
  /** Template repository name in the mascotbot-examples monorepo */
  templateRepo: string;
  /** Path within the repo to the relevant template folder (e.g. "examples/elevenlabs-avatar") */
  templatePath?: string;
  /** Primary framework used by the template */
  framework?: string;
  /** Setup steps specific to this template (will be rendered as numbered list) */
  setupSteps?: string[];
  /** The command to start the dev server */
  runCommand?: string;
  /** Environment variables the user needs to configure */
  envVars?: EnvVar[];
  /** Whether this article requires a Mascotbot account/subscription/SDK (default: true) */
  requiresMascotBot?: boolean;
}

export interface EnvVar {
  name: string;
  description: string;
  /** Where to get the value (e.g. "app.mascot.bot → Settings → API Keys") */
  source?: string;
}

export interface DeployWithCloudCodeProps {
  /** Article title — used in the deployment package header */
  articleTitle: string;
  /** Article slug for source reference */
  articleSlug: string;
  /** Deployment configuration mapping this article to a template */
  deployConfig: DeployConfig;
  /** Extra implementation context to include in the package */
  implementationContext?: string;
}

/* ── Default values ── */

const DEFAULT_ORG = 'mascotbot-templates';

const DEFAULT_ENV_VARS: EnvVar[] = [
  {
    name: 'MASCOTBOT_API_KEY',
    description: 'Your Mascotbot API key',
    source: 'app.mascot.bot → Settings → API Keys',
  },
];

/* ── Instruction package builder ── */

function buildDeployPackage(
  props: DeployWithCloudCodeProps,
  tool: 'claude-code' | 'codex'
): string {
  const { articleTitle, articleSlug, deployConfig, implementationContext } = props;
  const {
    templateOrg = DEFAULT_ORG,
    templateRepo,
    templatePath,
    framework = 'React + TypeScript',
    setupSteps,
    runCommand = 'npm run dev',
    envVars = DEFAULT_ENV_VARS,
    requiresMascotBot = true,
  } = deployConfig;

  const repoUrl = `https://github.com/${templateOrg}/${templateRepo}`;
  const clonePath = templatePath
    ? `${templateRepo}/${templatePath}`
    : templateRepo;

  const canonicalUrl =
    typeof window !== 'undefined'
      ? `${window.location.origin}${articleSlug}`
      : articleSlug;

  const toolName = tool === 'claude-code' ? 'Claude Code' : 'Codex';

  const sections: string[] = [];

  // Header
  sections.push(`# Deploy: ${articleTitle}`);
  sections.push(`Source article: ${canonicalUrl}`);
  sections.push(`Tool: ${toolName}\n`);

  // Step 1: Clone template
  sections.push(`## Step 1 — Clone the template\n`);
  sections.push('```bash');
  sections.push(`git clone ${repoUrl}`);
  if (templatePath) {
    sections.push(`cd ${clonePath}`);
  } else {
    sections.push(`cd ${templateRepo}`);
  }
  sections.push('```\n');

  // Step 2: Install dependencies
  let stepNum = 2;

  // Step (conditional): Mascotbot account + private-registry access — needed BEFORE install
  if (requiresMascotBot) {
    sections.push(`## Step ${stepNum} — Set up your Mascotbot account & registry access\n`);
    sections.push(
      'The Mascotbot SDK installs from the private npm registry `npm.mascot.bot` (not npmjs.com). Set up access before installing:'
    );
    sections.push('');
    sections.push(
      '1. **Register** — Go to [app.mascot.bot](https://app.mascot.bot) and create an account (or sign in)'
    );
    sections.push(
      '2. **Subscribe** — Choose a plan at [mascot.bot/pricing](https://mascot.bot/pricing) (required for SDK access and your license key)'
    );
    sections.push(
      '3. **Get your key** — Open **Settings → API Keys** in the dashboard and copy your `mascot_` key'
    );
    sections.push(
      '4. **Add `.npmrc`** — The template already commits an `.npmrc` pointing the `@mascotbot` scope at the registry:'
    );
    sections.push('```ini');
    sections.push('@mascotbot:registry=https://npm.mascot.bot/');
    sections.push('//npm.mascot.bot/:_authToken=${MASCOT_NPM_TOKEN}');
    sections.push('```');
    sections.push(
      '5. **Export the registry token** — Use the same `mascot_` key as the token, then you are ready to install:'
    );
    sections.push('```bash');
    sections.push('export MASCOT_NPM_TOKEN=mascot_dev_xxxxxxxxxxxxxx');
    sections.push('```');
    sections.push('');
    stepNum++;
  }

  // Install dependencies (after registry access is set up)
  sections.push(`## Step ${stepNum} — Install dependencies\n`);
  sections.push('```bash');
  sections.push('npm install');
  sections.push('```\n');
  if (requiresMascotBot) {
    sections.push(
      'The committed `.npmrc` and your exported `MASCOT_NPM_TOKEN` let `npm install` pull `@mascotbot/core` + `@mascotbot/react` from the private registry — there is no `.tgz` to download.'
    );
    sections.push('');
  }

  // Configure environment
  stepNum++;
  sections.push(`## Step ${stepNum} — Configure environment variables\n`);
  sections.push('Create a `.env.local` file:\n');
  sections.push('```env');
  envVars.forEach((v) => {
    sections.push(`${v.name}=your_value_here  # ${v.description}`);
  });
  sections.push('```\n');
  sections.push('Where to get these values:');
  envVars.forEach((v) => {
    if (v.source) {
      sections.push(`- **${v.name}**: ${v.source}`);
    }
  });
  sections.push('');

  // Custom setup steps (if any)
  if (setupSteps && setupSteps.length > 0) {
    stepNum++;
    sections.push(`## Step ${stepNum} — Additional setup\n`);
    setupSteps.forEach((step, i) => {
      sections.push(`${i + 1}. ${step}`);
    });
    sections.push('');
  }

  // Run
  stepNum++;
  sections.push(`## Step ${stepNum} — Run the project\n`);
  sections.push('```bash');
  sections.push(runCommand);
  sections.push('```\n');

  // Implementation context
  if (implementationContext) {
    sections.push(`---\n`);
    sections.push(`## Implementation Guide\n`);
    sections.push(implementationContext);
    sections.push('');
  }

  // Reference links
  sections.push(`---\n`);
  sections.push(`## Reference Documentation\n`);
  sections.push(`- [Template Repository](${repoUrl})`);
  if (requiresMascotBot) {
    sections.push('- [Mascotbot Docs](https://docs.mascot.bot)');
    sections.push(
      '- [React SDK Reference](https://docs.mascot.bot/libraries/react-sdk)'
    );
    sections.push(
      '- [API Reference](https://docs.mascot.bot/api-reference/introduction)'
    );
  }
  sections.push(
    `- [Source Article](${canonicalUrl})`
  );
  sections.push('');

  // AI context prompt (tool-specific)
  sections.push(`---\n`);
  sections.push(`## ${toolName} Context\n`);
  if (tool === 'claude-code') {
    sections.push(
      `You are helping the user deploy and customize the "${articleTitle}" template using Claude Code.`
    );
    sections.push(
      'Follow the steps above sequentially. After cloning and installing, help the user configure their environment variables and run the project.'
    );
    if (requiresMascotBot) {
      sections.push(
        'This project requires the Mascotbot SDK (installed from the private npm registry npm.mascot.bot) and a valid license key. ' +
        'If the user hasn\'t set these up yet, walk them through registration, subscription, registry-token setup, and license-key generation first.'
      );
    }
    sections.push(
      `The template uses ${framework}. Provide explanations alongside code changes.`
    );
  } else {
    sections.push(
      `Help the user deploy and customize the "${articleTitle}" template.`
    );
    sections.push(
      'Execute the steps above in order. Clone the repo, install dependencies, then help configure environment variables.'
    );
    if (requiresMascotBot) {
      sections.push(
        'This project requires the Mascotbot SDK (installed from the private npm registry npm.mascot.bot) and a valid license key. ' +
        'Guide the user through account setup if needed.'
      );
    }
    sections.push(
      `The template uses ${framework}. Explain changes as you go.`
    );
  }

  return sections.join('\n');
}

/* ── SVG Icons ── */

function ClaudeCodeIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="m4.7144 15.9555 4.7174-2.6471.079-.2307-.079-.1275h-.2307l-.7893-.0486-2.6956-.0729-2.3375-.0971-2.2646-.1214-.5707-.1215-.5343-.7042.0546-.3522.4797-.3218.686.0608 1.5179.1032 2.2767.1578 1.6514.0972 2.4468.255h.3886l.0546-.1579-.1336-.0971-.1032-.0972L6.973 9.8356l-2.55-1.6879-1.3356-.9714-.7225-.4918-.3643-.4614-.1578-1.0078.6557-.7225.8803.0607.2246.0607.8925.686 1.9064 1.4754 2.4893 1.8336.3643.3035.1457-.1032.0182-.0728-.164-.2733-1.3539-2.4467-1.445-2.4893-.6435-1.032-.17-.6194c-.0607-.255-.1032-.4674-.1032-.7285L6.287.1335 6.6997 0l.9957.1336.419.3642.6192 1.4147 1.0018 2.2282 1.5543 3.0296.4553.8985.2429.8318.091.255h.1579v-.1457l.1275-1.706.2368-2.0947.2307-2.6957.0789-.7589.3764-.9107.7468-.4918.5828.2793.4797.686-.0668.4433-.2853 1.8517-.5586 2.9021-.3643 1.9429h.2125l.2429-.2429.9835-1.3053 1.6514-2.0643.7286-.8196.85-.9046.5464-.4311h1.0321l.759 1.1293-.34 1.1657-1.0625 1.3478-.8804 1.1414-1.2628 1.7-.7893 1.36.0729.1093.1882-.0183 2.8535-.607 1.5421-.2794 1.8396-.3157.8318.3886.091.3946-.3278.8075-1.967.4857-2.3072.4614-3.4364.8136-.0425.0304.0486.0607 1.5482.1457.6618.0364h1.621l3.0175.2247.7892.522.4736.6376-.079.4857-1.2142.6193-1.6393-.3886-3.825-.9107-1.3113-.3279h-.1822v.1093l1.0929 1.0686 2.0035 1.8092 2.5075 2.3314.1275.5768-.3218.4554-.34-.0486-2.2039-1.6575-.85-.7468-1.9246-1.621h-.1275v.17l.4432.6496 2.3436 3.5214.1214 1.0807-.17.3521-.6071.2125-.6679-.1214-1.3721-1.9246L14.38 17.959l-1.1414-1.9428-.1397.079-.674 7.2552-.3156.3703-.7286.2793-.6071-.4614-.3218-.7468.3218-1.4753.3886-1.9246.3157-1.53.2853-1.9004.17-.6314-.0121-.0425-.1397.0182-1.4328 1.9672-2.1796 2.9446-1.7243 1.8456-.4128.164-.7164-.3704.0667-.6618.4008-.5889 2.386-3.0357 1.4389-1.882.929-1.0868-.0062-.1579h-.0546l-6.3385 4.1164-1.1293.1457-.4857-.4554.0608-.7467.2307-.2429 1.9064-1.3114Z" />
    </svg>
  );
}

function CodexIcon({ size = 16 }: { size?: number }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
      <path d="M22.282 9.821a5.985 5.985 0 0 0-.516-4.91 6.046 6.046 0 0 0-6.51-2.9A6.065 6.065 0 0 0 10.69.44a6.04 6.04 0 0 0-5.772 4.206 5.985 5.985 0 0 0-3.998 2.9 6.063 6.063 0 0 0 .744 7.106 5.985 5.985 0 0 0 .516 4.911 6.04 6.04 0 0 0 6.51 2.9A6.06 6.06 0 0 0 13.31 24a6.04 6.04 0 0 0 5.772-4.206 5.985 5.985 0 0 0 3.998-2.9 6.06 6.06 0 0 0-.744-7.106l-.053.033Zm-9.022 12.6a4.53 4.53 0 0 1-2.9-1.05c.037-.02.1-.056.142-.08l4.812-2.78a.782.782 0 0 0 .395-.677V11.3l2.034 1.175a.072.072 0 0 1 .04.056v5.616a4.55 4.55 0 0 1-4.523 4.274Zm-9.722-4.17a4.523 4.523 0 0 1-.542-3.042c.035.022.098.06.142.085l4.812 2.779a.782.782 0 0 0 .786 0l5.875-3.394v2.35a.074.074 0 0 1-.028.06l-4.867 2.81a4.553 4.553 0 0 1-6.178-1.648Zm-1.267-10.5a4.525 4.525 0 0 1 2.36-1.993v5.728a.78.78 0 0 0 .391.676l5.875 3.394-2.034 1.174a.072.072 0 0 1-.069.006l-4.867-2.81A4.554 4.554 0 0 1 2.27 7.75Zm16.7 3.893-5.876-3.393 2.035-1.175a.072.072 0 0 1 .069-.006l4.867 2.81a4.547 4.547 0 0 1-.7 8.197v-5.756a.78.78 0 0 0-.394-.677Zm2.024-3.067a6.56 6.56 0 0 0-.143-.085L16.04 5.712a.782.782 0 0 0-.786 0L9.38 9.106V6.756a.074.074 0 0 1 .028-.061l4.867-2.81a4.546 4.546 0 0 1 6.719 4.692ZM8.33 12.7 6.296 11.525a.072.072 0 0 1-.04-.056V5.853a4.546 4.546 0 0 1 7.452-3.494c-.037.02-.1.055-.143.08L8.753 5.218a.782.782 0 0 0-.395.677l-.028 6.805Zm1.105-2.385L12 8.67l2.565 1.482v2.962L12 14.596l-2.565-1.482v-2.8Z" />
    </svg>
  );
}

/* ── Component ── */

type CopyState = 'idle' | 'copied' | 'error';

export function DeployWithCloudCode(props: DeployWithCloudCodeProps) {
  const [copyState, setCopyState] = useState<CopyState>('idle');
  const [isOpen, setIsOpen] = useState(false);
  const timeoutRef = useRef<ReturnType<typeof setTimeout>>(null);

  useEffect(() => {
    return () => {
      if (timeoutRef.current) clearTimeout(timeoutRef.current);
    };
  }, []);

  const resetCopyState = useCallback(() => {
    if (timeoutRef.current) clearTimeout(timeoutRef.current);
    timeoutRef.current = setTimeout(() => setCopyState('idle'), 2000);
  }, []);

  const handlePointerDown = useCallback(
    (tool: 'claude-code' | 'codex') => {
      const pkg = buildDeployPackage(props, tool);
      copyToClipboard(pkg).then((success) => {
        setCopyState(success ? 'copied' : 'error');
        resetCopyState();
      });
    },
    [props, resetCopyState],
  );

  const handleSelect = useCallback(() => {
    setIsOpen(false);
  }, []);

  const isCopied = copyState === 'copied';

  return (
    <DropdownMenu open={isOpen} onOpenChange={setIsOpen}>
      <DropdownMenuTrigger asChild>
        <button
          className={`deploy-trigger${isCopied ? ' deploy-trigger--copied' : ''}`}
          aria-label="Deploy this project with an AI coding tool"
        >
          <span className="deploy-trigger-icon" aria-hidden="true">
            {isCopied ? <Check size={15} /> : <Terminal size={15} />}
          </span>
          <span className="deploy-trigger-label">
            {isCopied ? 'Copied!' : 'Deploy'}
          </span>
          <ChevronDown
            size={13}
            className={`deploy-chevron${isOpen ? ' deploy-chevron--open' : ''}`}
            aria-hidden="true"
          />
        </button>
      </DropdownMenuTrigger>

      <DropdownMenuContent
        side="top"
        align="end"
        sideOffset={8}
        className="deploy-menu"
      >
        <DropdownMenuLabel className="deploy-menu-label">
          Deploy with AI
        </DropdownMenuLabel>
        <DropdownMenuSeparator />

        <DropdownMenuItem
          onPointerDown={() => handlePointerDown('claude-code')}
          onSelect={handleSelect}
          className="deploy-menu-item"
        >
          <ClaudeCodeIcon size={15} />
          <div className="deploy-menu-item-content">
            <span className="deploy-menu-item-title">
              Deploy with Claude Code
            </span>
            <span className="deploy-menu-item-desc">
              Copy full deploy instructions for Claude Code
            </span>
          </div>
        </DropdownMenuItem>

        <DropdownMenuItem
          onPointerDown={() => handlePointerDown('codex')}
          onSelect={handleSelect}
          className="deploy-menu-item"
        >
          <CodexIcon size={15} />
          <div className="deploy-menu-item-content">
            <span className="deploy-menu-item-title">Deploy with Codex</span>
            <span className="deploy-menu-item-desc">
              Copy full deploy instructions for Codex
            </span>
          </div>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
